import { FunctionalComponent, h, Host } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import ResultsTemplate from './results/results.template';
import MessagesTemplate from './messages/messages.template';
import LoadingTemplate from './loading.template';
import JumpLinksTemplate from './results/jump-links.template';
import { SchemaName } from '@cewe-onsitesearch/schemas';
import { PlaceholderIcons } from '@cewe-designsystem/util_placeholder_icons';
import { OnsiteSearchComponent } from '../cwc-onsite-search';
import { OnsiteSearchState } from '../onsite-search-state.enum';

/**
 * possible positions of results in the overlay grid.
 */
export enum ResultGridPosition {
  right = 'right',
  left = 'left',
}

/**
 * rendering config for resultsets.
 */
type ResultConfig = {
  /**
   * defines the position of the resultset in the grid.
   */
  gridPosition?: ResultGridPosition;

  /**
   * if TRUE there will be a "show more" button to open more results.
   */
  showMore: boolean;

  /**
   * number of columns for this resultset, needed to calculate the initial visible items if showMore is TRUE.
   */
  col: number;
};

/**
 * index specific configurations for resultset rendering.
 */
type ResultStructure = {
  [key in SchemaName]?: ResultConfig;
};

/**
 * Definition of  parameters that the template needs to work.
 */
interface OnsiteSearchComponentTemplateProps {
  /**
   * The class instance that provides controller functionality for the template
   */
  controller: OnsiteSearchComponent;
  // Specify parameters that are required to be passed down to the template here.
}

const OnsiteSearchComponentTemplate: FunctionalComponent<OnsiteSearchComponentTemplateProps> = ({ controller }) => {
  // @TODO: extract in a config file
  const resultStructure: ResultStructure = {
    [SchemaName.product]: {
      showMore: true,
      col: 2,
    },
    [SchemaName.category]: {
      gridPosition: ResultGridPosition.right,
      showMore: controller.breakPoints.breakpointDown('sm'),
      col: 1,
    },
    [SchemaName.inspiration]: {
      showMore: true,
      col: 2,
    },
    [SchemaName.service]: {
      showMore: true,
      col: 2,
    },
    [SchemaName.page]: {
      showMore: true,
      col: 3,
    },
  };

  // reorder the index structure based on bestResultsOrder
  const resultsOrder: SchemaName[] = controller.searchResponse?.results?.bestResultsOrder ?? [];
  for (const index in resultStructure) {
    if (!resultsOrder.includes(index as SchemaName)) {
      resultsOrder.push(index as SchemaName);
    }
  }

  return (
    <Host>
      <search-form>
        <icon onClick={() => controller.close(false)}>
          {controller.breakPoints.breakpointUp('md') ? (
            <slot name={'close-icon'}>
              <svg viewBox={'0 0 24 24'}>{PlaceholderIcons.close()}</svg>
            </slot>
          ) : (
            <slot name={'mobile-close-icon'}>
              <svg viewBox={'0 0 24 24'}>{PlaceholderIcons.svgArrowLeft()}</svg>
            </slot>
          )}
        </icon>
        <slot name={'input'} />
      </search-form>
      <backdrop onClick={() => controller.close(false)}></backdrop>
      <overlay aria-hidden={controller.state != OnsiteSearchState.Opened ? 'true' : 'false'}>
        <overlay-content>
          {controller.loading ? (
            <LoadingTemplate textResources={controller.textResources} />
          ) : controller.activeModule ? (
            controller.activeModule.getContent(controller)
          ) : (
            [
              <JumpLinksTemplate controller={controller} order={resultsOrder} />,
              <gridcontainer>
                <separator></separator>
                <MessagesTemplate controller={controller} />
                {resultsOrder
                  .filter((resultType: SchemaName) => resultStructure?.[resultType])
                  .map((resultType: SchemaName) => (
                    <ResultsTemplate
                      controller={controller}
                      type={resultType}
                      searchResponse={controller.searchResponse}
                      gridPosition={resultStructure?.[resultType].gridPosition}
                      showMore={resultStructure?.[resultType].showMore ?? true}
                      initialVisibleRows={controller.configuration.initialSearchResultRows}
                      columns={resultStructure?.[resultType].col ?? 2}
                    />
                  ))}
              </gridcontainer>,
            ]
          )}
        </overlay-content>
      </overlay>
    </Host>
  );
};

export default OnsiteSearchComponentTemplate;
